/**
 * Hilfsfunktionen für Trello-Card-Titel-Bereinigung.
 * Entfernt Datum, generische Begriffe; extrahiert Schlüsselwörter aus Grund.
 */

const DATE_PATTERNS = [
  /\b\d{4}-\d{2}-\d{2}\b/g,           // 2026-04-29
  /\b\d{4}-\d{1,2}\b/g,                // 2026-04, 2026-4
  /\b\d{1,2}\.\d{1,2}\.\d{4}\b/g,     // 29.04.2026
  /\b\d{1,2}\.\d{1,2}\.\d{2}\b/g,     // 29.04.26
  /\b20\d{2}\b/g,                      // 2024, 2025, 2026 ...
  /\b\d{4}-\d{1,2}\s+bis\s+\d{4}-\d{1,2}\b/gi, // 2025-6 bis 2026-2
];

const STOP_WORDS_TITLE = new Set([
  "leerraum", "sonstiges", "sonstige", "bis", "und", "der", "die", "das",
  "von", "fur", "für", "mit", "bei", "an", "auf", "aus", "nach", "im",
]);

const STOP_WORDS_GRUND = new Set([
  "nicht", "wurde", "werden", "bitte", "mal", "ist", "ein", "eine",
  "der", "die", "das", "und", "oder", "für", "von", "zu", "mit",
  "bei", "an", "in", "auf", "aus", "nach", "aber", "auch", "dass",
  "sich", "noch", "bereits", "wenn", "dann", "durch", "es", "er",
  "sie", "wir", "ich", "du", "hat", "haben", "war", "sein", "den",
  "dem", "des", "eines", "einer", "kann", "muss", "soll", "wird",
]);

/**
 * Bereinigt einen Dateinamen für den Trello-Card-Titel.
 * - Entfernt Dateiendung
 * - Entfernt alle Datumsformate
 * - Entfernt Stop-Wörter (Leerraum, Sonstiges etc.)
 * - Bereinigt Trennzeichen
 */
export function cleanFilenameForTrello(filename) {
  let s = filename
    .replace(/\.pdf$/i, "")
    .replace(/\.\w{2,4}$/i, "");

  // Datum entfernen (längste zuerst für richtiger Reihenfolge)
  for (const pattern of DATE_PATTERNS) {
    s = s.replace(pattern, " ");
  }

  // Stop-Wörter entfernen (nur ganzes Wort)
  STOP_WORDS_TITLE.forEach(w => {
    s = s.replace(new RegExp(`(?<![\\w])${w}(?![\\w])`, "gi"), " ");
  });

  // Mehrfach-Trennzeichen und Leerzeichen bereinigen
  s = s
    .replace(/[\s\-–_]+/g, " ")
    .trim()
    .replace(/^[\s\-–]+|[\s\-–]+$/g, "")
    .trim();

  return s;
}

/**
 * Extrahiert 1–2 aussagekräftige Schlüsselwörter aus dem Grund/Anmerkungstext.
 */
export function extractGrundKeywords(grund) {
  if (!grund || !grund.trim()) return "";
  const words = grund
    .replace(/[^a-zA-ZäöüÄÖÜß\s]/g, " ")
    .split(/\s+/)
    .filter(w => w.length >= 4 && !STOP_WORDS_GRUND.has(w.toLowerCase()));
  if (!words.length) return "";
  return words.slice(0, 2).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Kombinierter Titel: bereinigter Dateiname + optionale Grund-Keywords.
 *
 * `opts` (Welle 12):
 *   - annotation: vom User eingegebene Anmerkung (z. B. „Briefkasten")
 *   - einheit:    Einheit aus Adressen-DB (z. B. „006-01")
 *   - raum:       Raum aus objekt_zimmer (z. B. „Bad")
 *
 * Annotation und Raum sind oft die aussagekräftigsten Marker und gehören
 * in den Titel — vorher landeten sie nur in der Card-Beschreibung. Die
 * Reihenfolge im Präfix-Block: `{einheit}-{raum}` (falls vorhanden) als
 * kompakter Tag, danach der bereinigte Dateiname, danach Annotation und
 * Keywords aus dem Grund.
 */
export function buildTrelloTitle(filename, grund, opts = {}) {
  const { annotation = "", einheit = "", raum = "" } = opts || {};
  const base = cleanFilenameForTrello(filename);
  const baseLow = base.toLowerCase();
  const hasInBase = (v) => v && baseLow.includes(String(v).toLowerCase());
  // Tag-Block (Einheit/Raum) nur, wenn er NICHT bereits im base steht —
  // sonst hätten wir „[006-01-Bad] 006-01-Bad - …" als Doppelung.
  const tagParts = [];
  if (einheit && !hasInBase(einheit)) tagParts.push(String(einheit).trim());
  if (raum    && !hasInBase(raum))    tagParts.push(String(raum).trim());
  const tag = tagParts.join("-");
  // Annotation: max 50 Zeichen, ebenfalls nur wenn nicht schon im base.
  const a = (annotation || "").trim();
  const annotationShort = a.length > 50 ? a.slice(0, 49).trim() + "…" : a;
  const annotationOut = annotationShort && !hasInBase(annotationShort) ? annotationShort : "";
  const keywords = extractGrundKeywords(grund);
  const trailingParts = [annotationOut, keywords].filter(Boolean);
  const trailing = trailingParts.join(" · ");
  const leading = tag ? `[${tag}] ${base}` : base;
  return trailing ? `${leading} – ${trailing}` : leading;
}
